class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-discount-form]');
    this.input = this.querySelector('[data-discount-input]');
    this.applyButton = this.querySelector('[data-discount-apply]');
    this.appliedContainer = this.querySelector('[data-discount-applied]');
    this.codesContainer = this.querySelector('[data-discount-codes]');
    this.loadingIndicator = this.querySelector('[data-discount-loading]');
    this.context = this.dataset.context || 'drawer';
    this.pageSectionId = this.closest('[data-section-id]')?.dataset.sectionId || 'cart';
    this.currentDiscounts = this.getCurrentDiscounts();

    this.debouncedApply = theme.utils.debounce(this.applyDiscountCode.bind(this), 500);
    this.debouncedRemove = theme.utils.debounce(this.removeDiscountCode.bind(this), 500);
    this.debouncedRemoveAll = theme.utils.debounce(this.removeAllDiscountCodes.bind(this), 500);
  }

  connectedCallback() {
    this._eventsController = new AbortController();
    this.attachEvents();
    this.checkAppliedDiscounts();
    this.subscribeToCartUpdates();
  }

  disconnectedCallback() {
    this._eventsController?.abort();
    this._cartUpdateUnsub?.();
  }

  attachEvents() {
    const { signal } = this._eventsController;

    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.debouncedApply();
    }, { signal });

    this.querySelectorAll('[data-discount-remove]').forEach((button) => {
      button.addEventListener('click', (e) => {
        const code = e.currentTarget.dataset.discountRemove;
        this.debouncedRemove(code);
      }, { signal });
    });

    this.querySelector('[data-discount-remove-all]')?.addEventListener('click', () => {
      this.debouncedRemoveAll();
    }, { signal });
  }

  subscribeToCartUpdates() {
    if (typeof theme !== 'undefined' && theme.pubsub) {
      this._cartUpdateUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_UPDATE, (data) => {
        if (data?.skipFetch) return;
        if (data?.cart) {
          this.syncAppliedDiscounts(data.cart);
          return;
        }
        this.refreshFromCart();
      });
    }
  }

  async refreshFromCart() {
    try {
      const sectionId = this.getSectionsToRender();
      const cart = await theme.cart.fetch({ sections: [sectionId], sectionsUrl: window.location.pathname });
      if (cart.sections) {
        this.renderSections(cart.sections);
        this.syncAppliedDiscounts(cart);
      }
    } catch (error) {
      console.error('Error refreshing cart discount:', error);
    }
  }

  getCurrentDiscounts() {
    const codes = [];
    this.querySelectorAll('[data-discount-code]').forEach((el) => {
      codes.push(el.dataset.discountCode);
    });
    return codes;
  }

  async applyDiscountCode() {
    const code = this.input.value.trim();

    if (!code) {
      this._setFooterErrorSlot(theme.locales.cart.footerError.invalidDiscount);
      return;
    }

    theme.cart.errors.clearFooter();
    this._clearFooterErrorSlot();
    this.setLoading(true);

    try {
      const newDiscounts = [...this.currentDiscounts, code].join(',');
      const sectionIds = this.getSectionsToRender();

      const response = await theme.cart.update(
        { discount: newDiscounts },
        { sections: [sectionIds], sectionsUrl: window.location.pathname }
      );

      if (response.sections) {
        const submittedEntry = (response.discount_codes || []).find(
          (dc) => dc.code.toLowerCase() === code.toLowerCase()
        );

        if (submittedEntry?.applicable === false) {
          this.renderSections(response.sections);
          const msg = theme.locales.cart.footerError.invalidDiscount;
          this._setFooterErrorSlot(msg);
          const newElement = document.querySelector(`cart-discount[data-context="${this.context}"]`);
          const newInput = newElement?.querySelector('[data-discount-input]');
          if (newInput) newInput.value = code;
          newElement?.querySelector('theme-accordion')?.open();
        } else {
          this.renderSections(response.sections);
          this.syncAppliedDiscounts(response);
          this.input.value = '';
          if (theme.settings?.cartActionToastsEnable) {
            theme.toast?.show({ type: 'success', message: theme.locales.cart.success?.discountApplied || theme.locales.cart.toast.couponApplied });
          }
          theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { cart: response, skipFetch: true });
          this.syncCartDrawer();
        }
      }
    } catch (error) {
      if (error?.type === 'generic' || !error?.type) {
        this._setFooterErrorSlot(theme.locales.cart.footerError.invalidDiscount);
      }
    } finally {
      this.setLoading(false);
    }
  }

  async removeDiscountCode(codeToRemove) {
    theme.cart.errors.clearFooter();
    this._clearFooterErrorSlot();
    this.setLoading(true);

    try {
      const remainingDiscounts = this.currentDiscounts
        .filter((code) => code !== codeToRemove)
        .join(',');
      const sectionIds = this.getSectionsToRender();

      const response = await theme.cart.update(
        { discount: remainingDiscounts },
        { sections: [sectionIds], sectionsUrl: window.location.pathname }
      );

      if (response.sections) {
        this.renderSections(response.sections);
        this.syncAppliedDiscounts(response);
        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { cart: response, skipFetch: true });
        this.syncCartDrawer();
      }
    } catch (error) {
      this._setFooterErrorSlot(theme.locales.cart.footerError.generic);
    } finally {
      this.setLoading(false);
    }
  }

  async removeAllDiscountCodes() {
    theme.cart.errors.clearFooter();
    this._clearFooterErrorSlot();
    this.setLoading(true);

    try {
      const sectionIds = this.getSectionsToRender();

      const response = await theme.cart.update(
        { discount: '' },
        { sections: [sectionIds], sectionsUrl: window.location.pathname }
      );

      if (response.sections) {
        this.renderSections(response.sections);
        this.syncAppliedDiscounts(response);
        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, { cart: response, skipFetch: true });
        this.syncCartDrawer();
      }
    } catch (error) {
      this._setFooterErrorSlot(theme.locales.cart.footerError.generic);
    } finally {
      this.setLoading(false);
    }
  }

  getSectionsToRender() {
    if (this.context === 'page') {
      return this.pageSectionId;
    }
    return 'cart-drawer';
  }

  async syncCartDrawer() {
    if (this.context !== 'page') return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    try {
      const data = await theme.cart.fetch({ sections: ['cart-drawer'], sectionsUrl: window.location.pathname });
      if (data.sections) {
        cartDrawer._renderSections(data.sections);
        cartDrawer._updateHeaderBadge(data);
      }
    } catch (error) {
      console.error('Error syncing cart drawer:', error);
    }
  }

  renderSections(sections) {
    const sectionKey = this.context === 'page' ? this.pageSectionId : 'cart-drawer';
    const html = sections[sectionKey];

    if (!html) return;

    const doc = theme.utils.domParser.parseFromString(html, 'text/html');

    if (this.context === 'page') {
      this.renderCartPage(doc);
    } else {
      this.renderCartDrawer(doc);
    }
  }

  renderCartPage(doc) {
    const newContent = doc.querySelector('[data-cart-content]');
    const currentContent = document.querySelector('[data-cart-content]');

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    }

    this.updateCartCount(doc);
  }

  renderCartDrawer(doc) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const newContent = doc.querySelector('[data-cart-drawer-content]');
    const currentContent = cartDrawer.querySelector('[data-cart-drawer-content]');

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
    }

    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = cartDrawer.querySelector('.cart-drawer__footer');

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }

    this.updateCartCount(doc);
    cartDrawer.updateFocusableElements?.();
  }

  updateCartCount(doc) {
    const newCount = doc.querySelector('[data-cart-count]');
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (newCount) {
        el.textContent = newCount.textContent;
      }
    });
  }

  setLoading(isLoading) {
    if (this.loadingIndicator) {
      this.loadingIndicator.hidden = !isLoading;
    }

    if (this.form) {
      const formElements = this.form.elements;
      Array.from(formElements).forEach((el) => {
        el.disabled = isLoading;
      });
    }

    this.querySelectorAll('[data-discount-remove], [data-discount-remove-all]').forEach((button) => {
      button.disabled = isLoading;
    });

    this.style.pointerEvents = isLoading ? 'none' : '';
    this.style.opacity = isLoading ? '0.6' : '1';
  }

  _footerErrorScope() {
    return this.context === 'page'
      ? document.querySelector('cart-page')
      : document.querySelector('cart-drawer');
  }

  _setFooterErrorSlot(message) {
    const slot = this._footerErrorScope()?.querySelector('[data-cart-footer-error]');
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = false;
  }

  _clearFooterErrorSlot() {
    const slot = this._footerErrorScope()?.querySelector('[data-cart-footer-error]');
    if (!slot) return;
    slot.hidden = true;
    slot.textContent = '';
  }

  getTranslation(key) {
    const translations = {
      remove_label: this.dataset.removeLabel,
      applied_at_checkout: this.dataset.appliedAtCheckout,
    };
    return translations[key] || '';
  }

  async checkAppliedDiscounts() {
    try {
      const cart = await theme.cart.fetch();
      this.syncAppliedDiscounts(cart);
    } catch (error) {
      console.error('Error checking applied discounts:', error);
    }
  }

  getExtraDiscounts(cart) {
    const cartLevelCodes = (cart.cart_level_discount_applications || [])
      .map((d) => (d.title || d.discount_application?.title || '').toLowerCase())
      .filter(Boolean);

    const lineLevelCodes = (cart.items || [])
      .flatMap((item) => item.line_level_discount_allocations || [])
      .map((allocation) => (allocation.discount_application?.title || '').toLowerCase())
      .filter(Boolean);

    const accountedCodes = [...cartLevelCodes, ...lineLevelCodes];

    return (cart.discount_codes || []).filter((dc) => {
      const isApplicable = dc.applicable;
      const isNotAccounted = !accountedCodes.includes(dc.code.toLowerCase());
      return isApplicable && isNotAccounted;
    });
  }

  syncAppliedDiscounts(cart) {
    this.currentDiscounts = (cart.discount_codes || [])
      .filter((dc) => dc.applicable)
      .map((dc) => dc.code);

    this.checkHiddenFreeShipping(cart);
    this.syncTotalsDiscountRows(cart);

    if (!this.codesContainer) return;

    const extraDiscounts = this.getExtraDiscounts(cart);

    this.codesContainer.querySelectorAll('.cart-discount__code--dynamic').forEach((chip) => chip.remove());

    const { signal } = this._eventsController;
    const removeLabel = this.getTranslation('remove_label');
    const iconTemplate = this.querySelector('[data-discount-icon]');

    extraDiscounts.forEach((dc) => {
      const chip = document.createElement('div');
      chip.className = 'cart-discount__code cart-discount__code--dynamic';
      chip.dataset.discountCode = dc.code;

      const label = document.createElement('span');
      label.className = 'cart-discount__code-text';
      label.textContent = dc.code;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-discount__code-remove';
      button.setAttribute('aria-label', `${removeLabel} ${dc.code}`);
      button.dataset.discountRemove = dc.code;
      button.addEventListener('click', (e) => {
        this.debouncedRemove(e.currentTarget.dataset.discountRemove);
      }, { signal });

      if (iconTemplate) {
        button.appendChild(iconTemplate.content.cloneNode(true));
      }

      chip.appendChild(label);
      chip.appendChild(button);
      this.codesContainer.appendChild(chip);
    });

    if (this.appliedContainer) {
      this.appliedContainer.hidden = this.codesContainer.children.length === 0;
    }
  }

  checkHiddenFreeShipping(cart) {
    if (!this.getExtraDiscounts(cart).length) return;

    requestAnimationFrame(() => this.forceFreeShippingState());
  }

  forceFreeShippingState() {
    document.querySelectorAll('[data-cart-shipping]').forEach((row) => {
      const value = row.querySelector('[data-cart-shipping-value]');
      if (value && row.dataset.freeLabel) value.textContent = row.dataset.freeLabel;
    });

    document.querySelectorAll('[data-total-without-shipping]').forEach((el) => {
      el.textContent = el.dataset.totalWithoutShipping;
    });

    document.querySelectorAll('.cart-free-shipping').forEach((el) => {
      const text = el.querySelector('.cart-free-shipping__text');
      if (text) text.textContent = el.dataset.reachedText;

      const bar = el.querySelector('.cart-free-shipping__bar');
      bar?.setAttribute('aria-valuenow', '100');

      const fill = el.querySelector('.cart-free-shipping__fill');
      if (fill) fill.style.width = '100%';
    });
  }

  syncTotalsDiscountRows(cart) {
    const extraDiscounts = this.getExtraDiscounts(cart);

    document.querySelectorAll('.cart-footer__totals, .cart-sticky-bar__panel-inner').forEach((scope) => {
      const existingCodes = Array.from(scope.querySelectorAll('[data-discount-code]')).map((row) =>
        row.dataset.discountCode.toLowerCase()
      );

      const pendingDiscounts = extraDiscounts.filter(
        (dc) => !existingCodes.includes(dc.code.toLowerCase())
      );

      if (!pendingDiscounts.length) return;

      const container = this.getDiscountsContainer(scope);
      pendingDiscounts.forEach((dc) => container.appendChild(this.buildDiscountRow(dc.code)));
    });
  }

  getDiscountsContainer(scope) {
    const existing = scope.querySelector('[data-cart-discounts]');
    if (existing) return existing;

    const container = document.createElement('div');
    container.className = 'cart-footer__discounts flex flex-col gap-xs';
    container.setAttribute('data-cart-discounts', '');

    const anchor = scope.querySelector('[data-cart-shipping]') || scope.querySelector('.cart-footer__total');
    if (anchor) {
      anchor.before(container);
    } else {
      scope.appendChild(container);
    }

    return container;
  }

  buildDiscountRow(code) {
    const row = document.createElement('div');
    row.className = 'cart-footer__discount';
    row.dataset.discountCode = code;

    const label = document.createElement('span');
    label.className = 'cart-footer__label';
    label.textContent = code;

    const value = document.createElement('span');
    value.className = 'cart-footer__value cart-footer__value--discount';
    value.setAttribute('data-cart-discount', '');
    value.textContent = '-';

    row.appendChild(label);
    row.appendChild(value);
    return row;
  }
}

customElements.define('cart-discount', CartDiscount);
